'use client';
import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { FIXTURE_PRESETS, estimateLumensFromWatts } from '@/lighting-app/data/lightingStandards';
import type { InspectWizardState } from './InspectWizardClient';

interface Props {
  state: InspectWizardState;
  onChange: (u: Partial<InspectWizardState>) => void;
  onBack: () => void;
  onInspect: () => void;
}

export default function InspectStepFixtures({ state, onChange, onBack, onInspect }: Props) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleWattsEstimate = (watts: string) => {
    onChange({ wattsPerFixture: watts });
    const estimated = estimateLumensFromWatts(parseFloat(watts) || 0, state.fixtureTypeId || 'led-bulb');
    onChange({ wattsPerFixture: watts, lumensPerFixture: String(estimated) });
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!state.existingCount || parseInt(state.existingCount) <= 0) errs.count = 'أدخل عدد المصابيح الموجودة';
    if (parseInt(state.existingCount) > 10000) errs.count = 'قيمة غير منطقية';
    if (!state.lumensPerFixture || parseFloat(state.lumensPerFixture) <= 0) errs.lumens = 'أدخل قيمة Lumen أو احسبها من الـ Watt';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-3 rounded-xl border-2 bg-card text-foreground font-tabular text-base focus:outline-none focus:border-secondary transition-all ${
      errors[field] ? 'border-danger' : 'border-border'
    }`;

  return (
    <div className="slide-up">
      <div className="text-center mb-8">
        <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
          بيانات الإضاءة الحالية
        </h2>
        <p className="text-muted-foreground">أدخل معلومات المصابيح الموجودة حالياً في {state.roomType?.nameAr}</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Count */}
        <div className="wizard-card p-6">
          <label className="block text-sm font-semibold text-foreground mb-1">
            عدد المصابيح الموجودة <span className="text-danger">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-3">كم مصباحاً مركّباً حالياً في المكان؟</p>
          <input
            type="number"
            min="1"
            max="10000"
            placeholder="مثال: 6"
            value={state.existingCount}
            onChange={(e) => onChange({ existingCount: e.target.value })}
            className={inputClass('count')}
          />
          {errors.count && <p className="text-danger text-xs mt-1">{errors.count}</p>}
        </div>

        {/* Know Lumens Toggle */}
        <div className="wizard-card p-6">
          <h3 className="font-bold text-foreground mb-4">هل تعرف قيمة Lumen لكل مصباح؟</h3>
          <div className="flex gap-3 mb-5">
            <button
              onClick={() => onChange({ knowLumens: true })}
              className={`flex-1 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${
                state.knowLumens ? 'border-secondary bg-gray-50 text-secondary' : 'border-border text-muted-foreground hover:border-secondary/40'
              }`}
            >
              نعم، أعرف Lumen
            </button>
            <button
              onClick={() => onChange({ knowLumens: false })}
              className={`flex-1 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${
                !state.knowLumens ? 'border-secondary bg-gray-50 text-secondary' : 'border-border text-muted-foreground hover:border-secondary/40'
              }`}
            >
              لا، أعرف فقط Watt
            </button>
          </div>

          {state.knowLumens ? (
            <div className="fade-in">
              <label className="block text-sm font-semibold text-foreground mb-1">
                Lumen لكل مصباح <span className="text-danger">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-2">موجود على ملصق المصباح أو العبوة</p>
              <input
                type="number"
                min="1"
                placeholder="مثال: 1200"
                value={state.lumensPerFixture}
                onChange={(e) => onChange({ lumensPerFixture: e.target.value })}
                className={inputClass('lumens')}
              />
              {errors.lumens && <p className="text-danger text-xs mt-1">{errors.lumens}</p>}
            </div>
          ) : (
            <div className="fade-in space-y-4">
              {/* Fixture type for better estimation */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">نوع المصباح</label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {FIXTURE_PRESETS.filter((f) => f.id !== 'manual').map((fixture) => (
                    <button
                      key={`inspect-fixture-${fixture.id}`}
                      onClick={() => {
                        onChange({ fixtureTypeId: fixture.id });
                        if (state.wattsPerFixture) {
                          const est = estimateLumensFromWatts(parseFloat(state.wattsPerFixture), fixture.id);
                          onChange({ fixtureTypeId: fixture.id, lumensPerFixture: String(est) });
                        }
                      }}
                      className={`p-2 rounded-xl border-2 text-center text-xs transition-all ${
                        state.fixtureTypeId === fixture.id
                          ? 'border-secondary bg-gray-50' :'border-border hover:border-secondary/40'
                      }`}
                    >
                      <div className="text-xl mb-1">{fixture.icon}</div>
                      <div className="font-semibold text-foreground">{fixture.nameAr}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">
                  Watt لكل مصباح <span className="text-danger">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">سيتم تقدير Lumen تلقائياً</p>
                <input
                  type="number"
                  min="1"
                  placeholder="مثال: 12"
                  value={state.wattsPerFixture}
                  onChange={(e) => handleWattsEstimate(e.target.value)}
                  className={inputClass('watts')}
                />
              </div>

              {state.lumensPerFixture && (
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 fade-in">
                  <div className="flex items-start gap-2">
                    <Info size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-yellow-800">
                      التقدير: كل مصباح ينتج تقريباً <strong className="font-tabular">{state.lumensPerFixture} Lumen</strong>. النتيجة تقريبية وقد تختلف حسب جودة المصباح.
                    </p>
                  </div>
                </div>
              )}
              {errors.lumens && <p className="text-danger text-xs">{errors.lumens}</p>}
            </div>
          )}
        </div>

        {/* Advanced Settings */}
        <div className="wizard-card overflow-hidden">
          <button
            onClick={() => onChange({ useAdvanced: !state.useAdvanced })}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Info size={16} className="text-secondary" />
              <span className="font-semibold text-foreground">إعدادات متقدمة</span>
              <span className="text-xs text-muted-foreground">(للمهندسين)</span>
            </div>
            {state.useAdvanced ? <ChevronUp size={20} className="text-muted-foreground" /> : <ChevronDown size={20} className="text-muted-foreground" />}
          </button>

          {state.useAdvanced && (
            <div className="px-6 pb-6 fade-in border-t border-border">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">Utilization Factor (UF)</label>
                  <p className="text-xs text-muted-foreground mb-2">الافتراضي: 0.65</p>
                  <input type="number" min="0.1" max="1" step="0.01" value={state.uf}
                    onChange={(e) => onChange({ uf: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-border bg-card text-foreground font-tabular focus:outline-none focus:border-secondary" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">Maintenance Factor (MF)</label>
                  <p className="text-xs text-muted-foreground mb-2">الافتراضي: 0.80</p>
                  <input type="number" min="0.1" max="1" step="0.01" value={state.mf}
                    onChange={(e) => onChange({ mf: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-border bg-card text-foreground font-tabular focus:outline-none focus:border-secondary" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8 max-w-2xl mx-auto">
        <button onClick={onBack} className="btn-secondary px-6 py-3 rounded-xl flex items-center gap-2">
          <ArrowRight size={18} />
          <span>السابق</span>
        </button>
        <button
          onClick={() => { if (validate()) onInspect(); }}
          className="px-8 py-3 rounded-xl font-bold text-white flex items-center gap-2 transition-all gradient-dark hover:opacity-90"
        >
          <span>افحص الإضاءة</span>
          <ArrowLeft size={18} />
        </button>
      </div>
    </div>
  );
}